// Approved Estimate Queue — Jul-2026 (Sales Transactions › under Estimates).
// Approved estimates appear automatically, one row per distinct Order No on
// the estimate's items (order + server captured at item entry). Each row's
// Order Status is user-managed (default "Not Approved"). Top section: POC ×
// Status amount pivot. Own permission: approved_estimate_queue (view/edit).
import { useCallback, useEffect, useRef, useState } from "react";
import { api, formatINR } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { PageHeader } from "@/components/ui-helpers";
import { toast } from "sonner";
import { AlertTriangle, ChevronLeft, ChevronRight, Download, ListChecks, Plus, RotateCw, Search, Settings2, Trash2, Upload, X } from "lucide-react";

const LIMIT = 50;
const STATUS_STYLE = {
  "Approved": "border-emerald-300 bg-emerald-50 text-emerald-800",
  "Not Approved": "border-slate-300 bg-slate-50 text-slate-700",
  "Order Not Received": "border-sky-300 bg-sky-50 text-sky-800",
  "Work Cancelled": "border-rose-300 bg-rose-50 text-rose-700",
  "Not Required": "border-amber-300 bg-amber-50 text-amber-800",
  "Other (Cancel)": "border-violet-300 bg-violet-50 text-violet-800",
  "Partial JMS Pulled": "border-orange-300 bg-orange-50 text-orange-800",
  "Complete JMS pulled": "border-cyan-300 bg-cyan-50 text-cyan-800",
  "JMS Completed": "border-green-300 bg-green-50 text-green-800",
  "JMS and Invoice Done": "border-indigo-300 bg-indigo-50 text-indigo-800",
};
const STATUS_THEME = {
  "Approved": { dot: "bg-emerald-500", head: "text-emerald-400", text: "text-emerald-700", cell: "bg-emerald-50/70" },
  "Not Approved": { dot: "bg-slate-400", head: "text-slate-300", text: "text-slate-600", cell: "bg-slate-50/70" },
  "Order Not Received": { dot: "bg-sky-500", head: "text-sky-400", text: "text-sky-700", cell: "bg-sky-50/70" },
  "Work Cancelled": { dot: "bg-rose-500", head: "text-rose-400", text: "text-rose-700", cell: "bg-rose-50/70" },
  "Not Required": { dot: "bg-amber-500", head: "text-amber-400", text: "text-amber-700", cell: "bg-amber-50/70" },
  "Other (Cancel)": { dot: "bg-violet-500", head: "text-violet-400", text: "text-violet-700", cell: "bg-violet-50/70" },
  "Partial JMS Pulled": { dot: "bg-orange-500", head: "text-orange-400", text: "text-orange-700", cell: "bg-orange-50/70" },
  "Complete JMS pulled": { dot: "bg-cyan-500", head: "text-cyan-400", text: "text-cyan-700", cell: "bg-cyan-50/70" },
  "JMS Completed": { dot: "bg-green-500", head: "text-green-400", text: "text-green-700", cell: "bg-green-50/70" },
  "JMS and Invoice Done": { dot: "bg-indigo-500", head: "text-indigo-400", text: "text-indigo-700", cell: "bg-indigo-50/70" },
};
const selCls = "border border-slate-300 px-2.5 py-2 text-xs bg-white focus:outline-none focus:border-orange-500";
// Custom (master-added) statuses get a deterministic colour from this palette.
const FALLBACK_STYLES = [
  "border-teal-300 bg-teal-50 text-teal-800",
  "border-indigo-300 bg-indigo-50 text-indigo-800",
  "border-fuchsia-300 bg-fuchsia-50 text-fuchsia-800",
  "border-cyan-300 bg-cyan-50 text-cyan-800",
  "border-lime-300 bg-lime-50 text-lime-800",
];
const FALLBACK_THEMES = [
  { dot: "bg-teal-500", head: "text-teal-400", text: "text-teal-700", cell: "bg-teal-50/70" },
  { dot: "bg-indigo-500", head: "text-indigo-400", text: "text-indigo-700", cell: "bg-indigo-50/70" },
  { dot: "bg-fuchsia-500", head: "text-fuchsia-400", text: "text-fuchsia-700", cell: "bg-fuchsia-50/70" },
  { dot: "bg-cyan-500", head: "text-cyan-400", text: "text-cyan-700", cell: "bg-cyan-50/70" },
  { dot: "bg-lime-500", head: "text-lime-400", text: "text-lime-700", cell: "bg-lime-50/70" },
];
const hashIdx = (s, n) => { let h = 0; for (const c of s || "") h = (h * 31 + c.charCodeAt(0)) >>> 0; return h % n; };
const styleFor = (s) => STATUS_STYLE[s] || FALLBACK_STYLES[hashIdx(s, FALLBACK_STYLES.length)];
const themeFor = (s) => STATUS_THEME[s] || FALLBACK_THEMES[hashIdx(s, FALLBACK_THEMES.length)];

// Aug-2026 (user request) — spreadsheet-style cell selection. Column order
// mirrors the table; accessors return the raw copy value for each cell.
const COLS = [
  { h: "Estimate No", get: (r) => r.estimate_number || "" },
  { h: "Estimate Date", get: (r) => r.estimate_date || "" },
  { h: "Order No", get: (r) => r.order_number || "" },
  { h: "Order Server", get: (r) => r.order_server || "" },
  { h: "Client POC", get: (r) => r.client_poc || "" },
  { h: "Client", get: (r) => r.client_name || "" },
  { h: "Order Amount", get: (r) => String(r.order_amount ?? "") },
  { h: "Order Status", get: (r) => r.order_status || "" },
];

function SelCell({ ri, ci, sel, onDown, onEnter, className, title, testid, children }) {
  const inSel = sel && ri >= sel.r1 && ri <= sel.r2 && ci >= sel.c1 && ci <= sel.c2;
  return (
    <td onMouseDown={(e) => onDown(e, ri, ci)} onMouseEnter={() => onEnter(ri, ci)} title={title}
      data-testid={testid}
      className={`${className} cursor-cell ${inSel ? "bg-orange-100/80 ring-1 ring-inset ring-orange-300" : ""}`}>
      {children}
    </td>
  );
}

export default function ApprovedEstimateQueue() {
  const { hasPerm } = useAuth();
  const canEdit = hasPerm("approved_estimate_queue", "edit");
  const [data, setData] = useState({ rows: [], total: 0, summary: { rows: [], status_totals: {}, grand_total: 0 }, statuses: [], servers: [], pocs: [], clients: [] });
  const [filters, setFilters] = useState({ order_server: "", client_poc: "", order_status: "", client: "", q: "" });
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState("");

  const load = useCallback(async (p = 0, f = filters) => {
    setLoading(true);
    try {
      const params = {
        skip: p * LIMIT, limit: LIMIT,
        ...(f.order_server && { order_server: f.order_server }),
        ...(f.client_poc && { client_poc: f.client_poc }),
        ...(f.order_status && { order_status: f.order_status }),
        ...(f.client && { client: f.client }),
        ...(f.q && { q: f.q }),
      };
      const r = await api.get("/approved-estimate-queue", { params });
      setData(r.data);
      setPage(p);
    } catch {
      toast.error("Failed to load Approved Estimate Queue");
    } finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { load(0); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const setF = (patch) => {
    const nf = { ...filters, ...patch };
    setFilters(nf);
    load(0, nf);
  };

  async function updateStatus(row, status) {
    const key = `${row.estimate_id}|${row.order_number}`;
    setSaving(key);
    try {
      await api.put("/approved-estimate-queue/status", {
        estimate_id: row.estimate_id, order_number: row.order_number, status,
      });
      toast.success(`${row.estimate_number}${row.order_number ? ` · ${row.order_number}` : ""} → ${status}`);
      load(page);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Failed to update order status");
    } finally { setSaving(""); }
  }

  const [exporting, setExporting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [review, setReview] = useState(null);
  const [applying, setApplying] = useState(false);
  const [showStatusMaster, setShowStatusMaster] = useState(false);
  const fileRef = useRef(null);

  // ── Spreadsheet-style cell selection (Aug-2026 user request) ──────────
  // Drag across cells → rectangular selection copied to the clipboard
  // (tab/newline separated, Excel-ready). Click a column header → whole
  // column copied. Highlight persists so the user can see what was copied.
  const [selRect, setSelRect] = useState(null); // {ar, ac, br, bc} anchor→brush
  const draggingRef = useRef(false);
  const norm = (s) => s && {
    r1: Math.min(s.ar, s.br), r2: Math.max(s.ar, s.br),
    c1: Math.min(s.ac, s.bc), c2: Math.max(s.ac, s.bc),
  };
  const nsel = norm(selRect);

  const copyRect = useCallback((n, rows, colLabel) => {
    if (!n || !rows.length) return;
    const lines = [];
    for (let ri = n.r1; ri <= Math.min(n.r2, rows.length - 1); ri++) {
      const cols = [];
      for (let ci = n.c1; ci <= n.c2; ci++) cols.push(COLS[ci].get(rows[ri]));
      lines.push(cols.join("\t"));
    }
    const text = lines.join("\n");
    const done = () => toast.success(colLabel
      ? `"${colLabel}" column copied — ${lines.length} value(s)`
      : `Copied ${lines.length} row(s) × ${n.c2 - n.c1 + 1} column(s) — paste anywhere`);
    // Synchronous execCommand FIRST — it works inside embedded frames and
    // on every browser during a user gesture, where the async Clipboard
    // API is often permission-blocked. Async API only as a fallback.
    let ok = false;
    try {
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.setAttribute("readonly", "");
      ta.style.position = "fixed";
      ta.style.left = "-9999px";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.focus();
      ta.select();
      ta.setSelectionRange(0, text.length);
      ok = document.execCommand("copy");
      ta.remove();
    } catch { ok = false; }
    if (ok) { done(); return; }
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(text).then(done,
        () => toast.error("Copy blocked by the browser — try again or use HTTPS/full-tab view"));
    } else {
      toast.error("Copy to clipboard is not supported in this browser");
    }
  }, []);

  const onCellDown = (e, ri, ci) => {
    if (e.button !== 0 || e.target.tagName === "SELECT" || e.target.tagName === "OPTION") return;
    e.preventDefault();
    draggingRef.current = true;
    setSelRect({ ar: ri, ac: ci, br: ri, bc: ci });
  };
  const onCellEnter = (ri, ci) => {
    if (!draggingRef.current) return;
    setSelRect((s) => (s ? { ...s, br: ri, bc: ci } : s));
  };
  useEffect(() => {
    const up = () => {
      if (!draggingRef.current) return;
      draggingRef.current = false;
      const n = norm(selRect);
      if (n && (n.r1 !== n.r2 || n.c1 !== n.c2)) copyRect(n, data.rows || []);
    };
    window.addEventListener("mouseup", up);
    return () => window.removeEventListener("mouseup", up);
  }, [selRect, data.rows, copyRect]); // eslint-disable-line react-hooks/exhaustive-deps

  const copyColumn = (ci) => {
    const rows = data.rows || [];
    if (!rows.length) return;
    const n = { r1: 0, r2: rows.length - 1, c1: ci, c2: ci };
    setSelRect({ ar: 0, ac: ci, br: rows.length - 1, bc: ci });
    copyRect(n, rows, COLS[ci].h);
  };

  async function downloadTemplate() {
    setExporting(true);
    try {
      const r = await api.get("/approved-estimate-queue/export-template", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `aeq_order_status_template_${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Blank template downloaded — fill Order No, Order Server & New Order Status, then upload");
    } catch {
      toast.error("Template download failed");
    } finally { setExporting(false); }
  }

  async function onUploadFile(e) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/approved-estimate-queue/bulk-validate", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (!r.data.valid_count && !r.data.error_count) {
        toast.info("No data rows found in the uploaded file");
      } else {
        setReview(r.data);
      }
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Upload validation failed");
    } finally { setUploading(false); }
  }

  async function applyReview() {
    const items = (review?.valid || []).filter((v) => !v.unchanged)
      .map((v) => ({ estimate_id: v.estimate_id, order_number: v.order_number, status: v.new_status }));
    if (!items.length) {
      toast.info("Nothing to update — all valid rows already have that status");
      setReview(null);
      return;
    }
    setApplying(true);
    try {
      const r = await api.post("/approved-estimate-queue/bulk-apply", { items });
      toast.success(`${r.data.updated} order status(es) updated${review.error_count ? ` · ${review.error_count} failed row(s) skipped` : ""}`);
      setReview(null);
      load(page);
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Bulk apply failed");
    } finally { setApplying(false); }
  }

  const statuses = data.statuses || [];
  const pages = Math.max(Math.ceil(data.total / LIMIT), 1);

  return (
    <div className="space-y-4" data-testid="aeq-page">
      <PageHeader icon={ListChecks} title="Approved Estimate Queue"
        subtitle="Approved estimates by order — track client order status" />

      {/* POC × Status summary pivot */}
      <div className="bg-white border border-slate-200 overflow-hidden shadow-sm" data-testid="aeq-summary">
        <div className="px-4 py-3 bg-[#0B1F3A] flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2.5">
            <span className="w-1.5 h-5 bg-orange-500" />
            <span className="text-[11px] uppercase tracking-[0.22em] font-bold text-white">
              Client POC × Order Status
            </span>
            <span className="text-[10px] uppercase tracking-wider text-slate-400 hidden sm:inline">Amount Summary</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[10px] uppercase tracking-wider text-slate-400">Grand Total</span>
            <span className="px-2.5 py-1 bg-orange-500/15 border border-orange-500/40 text-orange-300 text-xs font-black tabular-nums"
              data-testid="aeq-grand-total">{formatINR(data.summary.grand_total)}</span>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-slate-800">
                <th className="text-left px-4 py-2.5 text-[10px] uppercase tracking-wider font-bold text-slate-300">Client POC</th>
                {statuses.map((s) => {
                  const t = themeFor(s);
                  return (
                    <th key={s} className={`text-right px-3 py-2.5 text-[10px] uppercase tracking-wider font-bold whitespace-nowrap ${t.head}`}>
                      <span className="inline-flex items-center gap-1.5">
                        <span className={`w-1.5 h-1.5 rounded-full ${t.dot}`} />{s}
                      </span>
                    </th>
                  );
                })}
                <th className="text-right px-4 py-2.5 text-[10px] uppercase tracking-wider font-bold text-orange-400">Total</th>
              </tr>
            </thead>
            <tbody>
              {(data.summary.rows || []).map((r, idx) => (
                <tr key={r.poc} className={`border-t border-slate-100 hover:bg-orange-50/40 transition-colors ${idx % 2 ? "bg-slate-50/50" : "bg-white"}`}
                  data-testid={`aeq-summary-row-${r.poc}`}>
                  <td className="px-4 py-2 font-semibold text-slate-800 whitespace-nowrap">
                    <span className="inline-flex items-center gap-2">
                      <span className="w-5 h-5 rounded-full bg-[#0B1F3A] text-white text-[9px] font-black flex items-center justify-center uppercase">
                        {(r.poc || "?").charAt(0)}
                      </span>
                      {r.poc}
                    </span>
                  </td>
                  {statuses.map((s) => {
                    const t = themeFor(s);
                    const v = r.by_status[s];
                    return (
                      <td key={s} className={`px-3 py-2 text-right tabular-nums ${v > 0 ? `font-bold ${t.text} ${t.cell}` : "text-slate-300"}`}>
                        {v > 0 ? formatINR(v) : "—"}
                      </td>
                    );
                  })}
                  <td className="px-4 py-2 text-right tabular-nums font-black text-slate-900 bg-slate-100/60">{formatINR(r.total)}</td>
                </tr>
              ))}
              {(data.summary.rows || []).length > 0 && (
                <tr className="bg-[#0B1F3A]">
                  <td className="px-4 py-2.5 text-[10px] uppercase tracking-wider font-bold text-slate-300">Total</td>
                  {statuses.map((s) => {
                    const t = themeFor(s);
                    const v = data.summary.status_totals[s];
                    return (
                      <td key={s} className={`px-3 py-2.5 text-right tabular-nums font-black ${v > 0 ? t.head : "text-slate-600"}`}>
                        {v > 0 ? formatINR(v) : "—"}
                      </td>
                    );
                  })}
                  <td className="px-4 py-2.5 text-right tabular-nums font-black text-orange-400">{formatINR(data.summary.grand_total)}</td>
                </tr>
              )}
              {!(data.summary.rows || []).length && (
                <tr><td colSpan={statuses.length + 2} className="px-4 py-4 text-center text-slate-400">No approved estimates found</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Filters + list */}
      <div className="bg-white border border-slate-200">
        <div className="flex gap-2 px-4 py-3 items-center flex-wrap border-b border-slate-100">
          <select data-testid="aeq-filter-server" value={filters.order_server}
            onChange={(e) => setF({ order_server: e.target.value })} className={selCls + " w-44"}>
            <option value="">All Order Servers</option>
            {(data.servers || []).map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <select data-testid="aeq-filter-poc" value={filters.client_poc}
            onChange={(e) => setF({ client_poc: e.target.value })} className={selCls + " w-44"}>
            <option value="">All Client POCs</option>
            {(data.pocs || []).map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
          <select data-testid="aeq-filter-client" value={filters.client}
            onChange={(e) => setF({ client: e.target.value })} className={selCls + " w-48"}>
            <option value="">All Clients</option>
            {(data.clients || []).map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <select data-testid="aeq-filter-status" value={filters.order_status}
            onChange={(e) => setF({ order_status: e.target.value })} className={selCls + " w-40"}>
            <option value="">All Order Status</option>
            {statuses.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <div className="relative w-56">
            <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <input data-testid="aeq-search" value={filters.q}
              onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
              onKeyDown={(e) => e.key === "Enter" && load(0)}
              placeholder="Search estimate / order / client…"
              className="w-full border border-slate-300 pl-8 pr-7 py-2 text-xs bg-white focus:outline-none focus:border-orange-500" />
            {filters.q && (
              <button data-testid="aeq-search-clear" title="Clear search"
                onClick={() => setF({ q: "" })}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700">
                <X size={13} />
              </button>
            )}
          </div>
          <button data-testid="aeq-apply" onClick={() => load(0)}
            className="bg-slate-900 text-white px-3 py-2 text-xs uppercase tracking-wider font-bold flex items-center gap-1.5 hover:bg-slate-800">
            <RotateCw size={12} className={loading ? "animate-spin" : ""} /> Apply
          </button>
          <button data-testid="aeq-download-template" onClick={downloadTemplate} disabled={exporting}
            className="border border-slate-300 text-slate-700 px-3 py-2 text-xs uppercase tracking-wider font-bold flex items-center gap-1.5 hover:bg-slate-50 disabled:opacity-50">
            <Download size={12} className={exporting ? "animate-bounce" : ""} /> {exporting ? "Preparing…" : "Template"}
          </button>
          {canEdit && (
            <>
              <input ref={fileRef} type="file" accept=".xlsx" className="hidden"
                data-testid="aeq-upload-input" onChange={onUploadFile} />
              <button data-testid="aeq-upload-btn" onClick={() => fileRef.current?.click()} disabled={uploading}
                className="bg-orange-600 text-white px-3 py-2 text-xs uppercase tracking-wider font-bold flex items-center gap-1.5 hover:bg-orange-700 disabled:opacity-50">
                <Upload size={12} className={uploading ? "animate-pulse" : ""} /> {uploading ? "Uploading…" : "Upload Excel"}
              </button>
              <button data-testid="aeq-manage-status-btn" onClick={() => setShowStatusMaster(true)}
                className="border border-slate-300 text-slate-700 px-3 py-2 text-xs uppercase tracking-wider font-bold flex items-center gap-1.5 hover:bg-slate-50">
                <Settings2 size={12} /> Manage Status
              </button>
            </>
          )}
          <div className="ml-auto text-[11px] text-slate-500 text-right" data-testid="aeq-total">
            {data.total} order row(s)
            <div className="text-[10px] text-slate-400">Tip: drag across cells or click a column header to copy column-wise</div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-xs select-none">
            <thead>
              <tr className="bg-slate-50 text-slate-500">
                {COLS.map((c, ci) => (
                  <th key={c.h} onClick={() => copyColumn(ci)}
                    data-testid={`aeq-col-head-${ci}`}
                    title={`Click to copy the whole "${c.h}" column`}
                    className={`px-3 py-2 text-[10px] uppercase tracking-wider font-bold whitespace-nowrap cursor-pointer hover:bg-orange-50 hover:text-orange-700 ${c.h === "Order Amount" ? "text-right" : "text-left"}`}>{c.h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={8} className="px-4 py-8 text-center text-slate-400">Loading…</td></tr>}
              {!loading && !(data.rows || []).length && (
                <tr><td colSpan={8} className="px-4 py-8 text-center text-slate-400">No rows match the current filters</td></tr>
              )}
              {!loading && (data.rows || []).map((r, i) => {
                const key = `${r.estimate_id}|${r.order_number}`;
                const cp = { sel: nsel, onDown: onCellDown, onEnter: onCellEnter, ri: i };
                return (
                  <tr key={key + i} className="border-t border-slate-50 hover:bg-slate-50/50"
                    data-testid={`aeq-row-${r.estimate_number}-${r.order_number || "none"}`}>
                    <SelCell {...cp} ci={0} className="px-3 py-2 font-semibold text-slate-800 whitespace-nowrap">{r.estimate_number}</SelCell>
                    <SelCell {...cp} ci={1} className="px-3 py-2 whitespace-nowrap">{r.estimate_date}</SelCell>
                    <SelCell {...cp} ci={2} className="px-3 py-2 whitespace-nowrap">{r.order_number || <span className="text-slate-300">—</span>}</SelCell>
                    <SelCell {...cp} ci={3} className="px-3 py-2">{r.order_server || <span className="text-slate-300">—</span>}</SelCell>
                    <SelCell {...cp} ci={4} className="px-3 py-2 whitespace-nowrap">{r.client_poc || <span className="text-slate-300">—</span>}</SelCell>
                    <SelCell {...cp} ci={5} className="px-3 py-2 max-w-[220px] truncate" title={r.client_name}>{r.client_name}</SelCell>
                    <SelCell {...cp} ci={6} className="px-3 py-2 text-right tabular-nums font-bold">{formatINR(r.order_amount)}</SelCell>
                    <SelCell {...cp} ci={7} className="px-3 py-2">
                      <select
                        data-testid={`aeq-status-${r.estimate_number}-${r.order_number || "none"}`}
                        value={r.order_status}
                        disabled={!canEdit || saving === key}
                        onChange={(e) => updateStatus(r, e.target.value)}
                        className={`border px-2 py-1 text-[11px] font-bold focus:outline-none disabled:opacity-60 ${styleFor(r.order_status)}`}>
                        {statuses.map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </SelCell>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between px-4 py-2.5 border-t border-slate-100">
          <div className="text-[11px] text-slate-500">Page {page + 1} of {pages}</div>
          <div className="flex gap-1.5">
            <button data-testid="aeq-prev" disabled={page === 0} onClick={() => load(page - 1)}
              className="border border-slate-300 p-1.5 disabled:opacity-40 hover:bg-slate-50"><ChevronLeft size={13} /></button>
            <button data-testid="aeq-next" disabled={page + 1 >= pages} onClick={() => load(page + 1)}
              className="border border-slate-300 p-1.5 disabled:opacity-40 hover:bg-slate-50"><ChevronRight size={13} /></button>
          </div>
        </div>
      </div>
      {showStatusMaster && (
        <StatusMasterModal onClose={() => setShowStatusMaster(false)} onChanged={() => load(page)} />
      )}
      {review && (
        <BulkReviewModal review={review} applying={applying}
          onApply={applyReview} onDiscard={() => { setReview(null); toast.info("Upload discarded — nothing was changed"); }} />
      )}
    </div>
  );
}

/** BulkReviewModal — Aug-2026 (user request). After uploading the filled
 *  blank template, the parsed rows are shown for review: valid rows can be
 *  applied while failed rows are listed with reasons; or discard everything. */
function BulkReviewModal({ review, applying, onApply, onDiscard }) {
  const valid = review.valid || [];
  const errors = review.errors || [];
  const changing = valid.filter((v) => !v.unchanged).length;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="aeq-bulk-review-modal">
      <div className="bg-white shadow-2xl w-full max-w-3xl max-h-[88vh] flex flex-col overflow-hidden border border-slate-200">
        <div className="flex items-center justify-between px-5 py-3.5 bg-[#0B1F3A]">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-orange-400">Bulk Order Status Update</div>
            <div className="text-sm font-black text-white">Review Before Applying</div>
          </div>
          <div className="flex items-center gap-2 text-[11px]">
            <span className="px-2 py-0.5 bg-emerald-500/20 border border-emerald-400/40 text-emerald-300 font-bold" data-testid="aeq-review-valid-count">{valid.length} valid</span>
            <span className="px-2 py-0.5 bg-sky-500/20 border border-sky-400/40 text-sky-300 font-bold">{changing} will change</span>
            <span className="px-2 py-0.5 bg-rose-500/20 border border-rose-400/40 text-rose-300 font-bold" data-testid="aeq-review-error-count">{errors.length} failed</span>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto">
          {errors.length > 0 && (
            <div className="border-b border-rose-100">
              <div className="px-5 py-2 bg-rose-50 text-[10px] uppercase tracking-wider font-bold text-rose-700 flex items-center gap-1.5">
                <AlertTriangle size={11} /> Failed rows — these will be SKIPPED
              </div>
              <div className="max-h-48 overflow-y-auto" data-testid="aeq-review-errors">
                {errors.map((er, i) => (
                  <div key={i} className="flex items-start gap-2 px-5 py-1.5 text-[11px] border-b border-rose-50 last:border-0">
                    <span className="font-bold text-rose-600 shrink-0">Row {er.row}</span>
                    <span className="text-slate-700">
                      {er.estimate_no ? <span className="font-semibold">{er.estimate_no}</span> : null}
                      {er.order_number ? <> · {er.order_number}</> : null}
                      {" — "}{er.reason}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {valid.length > 0 ? (
            <table className="w-full text-[11px]" data-testid="aeq-review-valid">
              <thead className="sticky top-0 bg-slate-50 border-b border-slate-200">
                <tr className="text-[9px] uppercase tracking-wider text-slate-400">
                  <th className="text-left px-5 py-2">Estimate No</th>
                  <th className="text-left px-3 py-2">Order No</th>
                  <th className="text-left px-3 py-2">Date</th>
                  <th className="text-left px-3 py-2">Current</th>
                  <th className="text-left px-3 py-2">New Status</th>
                </tr>
              </thead>
              <tbody>
                {valid.map((v, i) => (
                  <tr key={i} className={`border-b border-slate-50 ${v.unchanged ? "opacity-50" : ""}`}>
                    <td className="px-5 py-1.5 font-semibold text-slate-800 whitespace-nowrap">{v.estimate_number}</td>
                    <td className="px-3 py-1.5 whitespace-nowrap">{v.order_number || "—"}</td>
                    <td className="px-3 py-1.5 whitespace-nowrap">{v.estimate_date}</td>
                    <td className="px-3 py-1.5"><span className={`border px-1.5 py-0.5 text-[10px] font-bold ${styleFor(v.current_status)}`}>{v.current_status}</span></td>
                    <td className="px-3 py-1.5">
                      <span className={`border px-1.5 py-0.5 text-[10px] font-bold ${styleFor(v.new_status)}`}>{v.new_status}</span>
                      {v.unchanged && <span className="ml-1.5 text-[9px] uppercase text-slate-400 font-bold">no change</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="py-10 text-center text-slate-400 text-xs" data-testid="aeq-review-none-valid">
              No valid rows found — fix the failed rows in the file and upload again.
            </div>
          )}
        </div>
        <div className="flex items-center gap-2 px-5 py-3 bg-slate-50 border-t border-slate-200">
          <div className="text-[10px] text-slate-500 mr-auto">
            {errors.length > 0
              ? "Failed rows are skipped when applying. Fix them in the file and re-upload, or apply the valid rows now."
              : "All rows validated successfully."}
          </div>
          <button type="button" data-testid="aeq-review-discard" onClick={onDiscard} disabled={applying}
            className="border border-slate-300 text-slate-700 px-3 py-2 text-xs uppercase tracking-wider font-bold hover:bg-slate-100 disabled:opacity-50">
            Discard Upload
          </button>
          <button type="button" data-testid="aeq-review-apply" onClick={onApply} disabled={applying || changing === 0}
            className="bg-emerald-700 text-white px-4 py-2 text-xs uppercase tracking-wider font-bold hover:bg-emerald-800 disabled:opacity-50">
            {applying ? "Updating…" : `Update ${changing} Row(s)`}
          </button>
        </div>
      </div>
    </div>
  );
}

/** StatusMasterModal — Aug-2026 (user request). Page-edit users manage the
 *  Order Status master right here: add new statuses, delete unused custom
 *  ones. Built-ins are locked. */
function StatusMasterModal({ onClose, onChanged }) {
  const [items, setItems] = useState(null);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);

  const fetchList = () =>
    api.get("/approved-estimate-queue/statuses")
      .then((r) => setItems(r.data))
      .catch(() => { toast.error("Failed to load status master"); onClose(); });
  useEffect(() => { fetchList(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function addStatus(e) {
    e?.preventDefault();
    const v = name.trim();
    if (!v) return;
    setBusy(true);
    try {
      await api.post("/approved-estimate-queue/statuses", { name: v });
      toast.success(`Status "${v}" added`);
      setName("");
      await fetchList();
      onChanged?.();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Failed to add status");
    } finally { setBusy(false); }
  }

  async function removeStatus(it) {
    if (!window.confirm(`Delete status "${it.name}" from the master?`)) return;
    setBusy(true);
    try {
      await api.delete(`/approved-estimate-queue/statuses/${it.id}`);
      toast.success(`Status "${it.name}" deleted`);
      await fetchList();
      onChanged?.();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Failed to delete status");
    } finally { setBusy(false); }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="aeq-status-master-modal" onClick={onClose}>
      <div className="bg-white shadow-2xl w-full max-w-md max-h-[85vh] flex flex-col overflow-hidden border border-slate-200" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3.5 bg-[#0B1F3A]">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-orange-400">Order Status Master</div>
            <div className="text-sm font-black text-white">Manage Statuses</div>
          </div>
          <button type="button" onClick={onClose} data-testid="aeq-status-master-close"
            className="text-slate-300 hover:text-white p-1"><X size={16} /></button>
        </div>
        <form onSubmit={addStatus} className="flex gap-2 px-5 py-3 border-b border-slate-100 bg-slate-50/70">
          <input data-testid="aeq-status-master-input" value={name} maxLength={40}
            onChange={(e) => setName(e.target.value)}
            placeholder="New status name…"
            className="flex-1 border border-slate-300 px-3 py-2 text-xs bg-white focus:outline-none focus:border-orange-500" />
          <button type="submit" data-testid="aeq-status-master-add" disabled={busy || !name.trim()}
            className="bg-orange-600 text-white px-3 py-2 text-xs uppercase tracking-wider font-bold flex items-center gap-1.5 hover:bg-orange-700 disabled:opacity-50">
            <Plus size={12} /> Add
          </button>
        </form>
        <div className="flex-1 overflow-y-auto">
          {items === null ? (
            <div className="py-10 text-center text-slate-400 text-xs">Loading…</div>
          ) : items.map((it) => (
            <div key={it.id} className="flex items-center gap-2 px-5 py-2.5 border-b border-slate-50 hover:bg-slate-50/60"
              data-testid={`aeq-status-master-row-${it.name}`}>
              <span className={`inline-block border px-2 py-0.5 text-[11px] font-bold ${styleFor(it.name)}`}>{it.name}</span>
              {it.is_system ? (
                <span className="text-[9px] uppercase tracking-wider font-bold text-slate-400 border border-slate-200 px-1.5 py-0.5">Built-in</span>
              ) : (
                <span className="text-[10px] text-slate-400">by {it.created_by || "—"}</span>
              )}
              {it.in_use && <span className="text-[9px] uppercase tracking-wider font-bold text-emerald-600">In use</span>}
              {!it.is_system && (
                <button type="button" data-testid={`aeq-status-master-delete-${it.name}`}
                  onClick={() => removeStatus(it)} disabled={busy}
                  title={it.in_use ? "In use — reassign rows first" : "Delete status"}
                  className="ml-auto text-slate-400 hover:text-rose-600 disabled:opacity-40 p-1">
                  <Trash2 size={13} />
                </button>
              )}
            </div>
          ))}
        </div>
        <div className="px-5 py-2.5 bg-slate-50 border-t border-slate-100 text-[10px] text-slate-500">
          New statuses appear instantly in every dropdown, the summary pivot and the bulk-upload template. Built-in statuses cannot be deleted; custom statuses can only be deleted while unused.
        </div>
      </div>
    </div>
  );
}
